import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { authenticateWithPasskey } from '../utils/passkeyUtils'
import { getGoogleOAuthUrl } from '../config/oauth'

const Login = () => {
  const { isNight } = useTheme()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const { login, handlePasskeyLogin: authHandlePasskeyLogin } = useAuth()
  const navigate = useNavigate()

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const result = await login(formData.email, formData.password)

    if (result.success) {
      if (result.user?.role === 'admin') {
        navigate('/admin')
      } else {
        navigate('/portal')
      }
    } else {
      setError(result.message || 'Invalid email or password')

      if (result.status === 'pending') {
        setTimeout(() => {
          navigate('/pending')
        }, 2000)
      }
    }

    setLoading(false)
  }

  const handlePasskeyLogin = async () => {
    if (!formData.email) {
      setError('Please enter your email address first')
      return
    }

    setPasskeyLoading(true)
    setError('')

    const result = await authenticateWithPasskey(formData.email)

    if (result.success) {
      // Use AuthContext handler to set up token refresh
      authHandlePasskeyLogin(result.user, result.token, result.expiresIn)

      if (result.user?.role === 'admin') {
        navigate('/admin')
      } else {
        navigate('/portal')
      }
    } else {
      setError(result.message || 'Passkey authentication failed')

      if (result.status === 'pending') {
        setTimeout(() => {
          navigate('/pending')
        }, 2000)
      }
    }

    setPasskeyLoading(false)
  }

  return (
    <div className={`min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 ${isNight ? 'bg-void' : 'bg-gradient-to-b from-gray-50 to-gray-100'}`}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className={`text-center text-3xl font-bold ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
          {isNight ? 'ACCESS PORTAL' : 'Sign in to your account'}
        </h2>
        <p className={`mt-2 text-center text-sm ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
          {isNight ? 'Enter credentials to authenticate' : 'Access your customer portal'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md px-4">
        <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className={`block text-sm font-medium ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`mt-1 ${isNight ? 'glass-input-night' : 'glass-input'}`}
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className={`block text-sm font-medium ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className={`mt-1 ${isNight ? 'glass-input-night' : 'glass-input'}`}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className={`p-3 rounded-lg text-sm backdrop-blur-sm ${isNight ? 'bg-red-900/40 text-red-400 border border-red-500/30' : 'bg-red-50/80 text-red-800 border border-red-200/50'}`}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full disabled:opacity-50 disabled:cursor-not-allowed ${isNight ? 'btn-neon' : 'btn-primary'}`}
            >
              {loading ? 'Signing in...' : isNight ? 'AUTHENTICATE' : 'Sign in'}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className={`w-full border-t ${isNight ? 'border-cyber-cyan/20' : 'border-gray-200/50'}`}></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-3 ${isNight ? 'bg-transparent text-gray-500' : 'bg-transparent text-gray-500'}`}>
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <button
                onClick={handlePasskeyLogin}
                disabled={passkeyLoading || !formData.email}
                className={`w-full flex items-center justify-center px-4 py-3 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-md ${
                  isNight
                    ? 'bg-void-elevated/50 border border-cyber-cyan/20 text-gray-300 hover:border-cyber-cyan/50 hover:text-cyber-cyan hover:bg-void-elevated/70'
                    : 'bg-white/50 border border-gray-200/50 text-gray-700 hover:bg-white/70 hover:border-primary-300'
                }`}
              >
                <span className="mr-2">🔑</span>
                {passkeyLoading ? 'Authenticating...' : 'Sign in with Passkey'}
              </button>

              <a
                href={getGoogleOAuthUrl()}
                className={`w-full flex items-center justify-center px-4 py-3 rounded-lg text-sm font-medium transition-all backdrop-blur-md ${
                  isNight
                    ? 'bg-void-elevated/50 border border-cyber-cyan/20 text-gray-300 hover:border-cyber-cyan/50 hover:text-cyber-cyan hover:bg-void-elevated/70'
                    : 'bg-white/50 border border-gray-200/50 text-gray-700 hover:bg-white/70 hover:border-primary-300'
                }`}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </a>
            </div>

            <p className={`mt-6 text-center text-sm ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
              Don't have an account?{' '}
              <Link to="/register" className={`font-medium hover:underline ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
